import axios from 'axios';
import { basicAuthConfig } from '../configs';
import { urlMyspreadApi } from '../urls';

/**
 * Obtiene la lista de todos los usuarios con sus permisos
 * @param {Object} user - Usuario autenticado
 * @returns {Promise<Object>} - Lista de usuarios con permisos
 */
export const getUsuarios = async (user) => {
    try {
        const config = basicAuthConfig(user);
        const response = await axios.get(
            `${urlMyspreadApi}/mantenedores/usuarios/`,
            config
        );
        
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error al obtener usuarios:', error);
        return {
            success: false,
            error: error.response?.data?.message || 'Error al obtener usuarios'
        };
    }
};

/**
 * Obtiene la lista de todos los permisos disponibles
 * @param {Object} user - Usuario autenticado
 * @returns {Promise<Object>} - Lista de permisos
 */
export const getPermisos = async (user) => {
    try {
        const config = basicAuthConfig(user);
        const response = await axios.get(
            `${urlMyspreadApi}/mantenedores/usuarios/permisos`,
            config
        );
        
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error al obtener permisos:', error);
        return {
            success: false,
            error: error.response?.data?.message || 'Error al obtener permisos'
        };
    }
};

/**
 * Obtiene la lista de todos los módulos disponibles
 * @param {Object} user - Usuario autenticado
 * @returns {Promise<Object>} - Lista de módulos
 */
export const getModulos = async (user) => {
    try {
        const config = basicAuthConfig(user);
        const response = await axios.get(
            `${urlMyspreadApi}/mantenedores/usuarios/roles`,
            config
        );
        
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error al obtener módulos:', error);
        return {
            success: false,
            error: error.response?.data?.message || 'Error al obtener módulos'
        };
    }
};

/**
 * Actualiza los permisos de un usuario específico
 * @param {Object} user - Usuario autenticado
 * @param {number} userId - ID del usuario a modificar
 * @param {string} modulo - Nombre del módulo
 * @param {string} permiso - Nombre del permiso
 * @param {string} action - 'add' para agregar, 'remove' para quitar
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const updateUserPermission = async (user, userId, modulo, permiso, action = 'add') => {
    try {
        const config = basicAuthConfig(user);
        const url = `${urlMyspreadApi}/mantenedores/usuarios/${userId}/permisos`;
        const data = { modulo, permiso };

        console.log(`🔄 ${action === 'add' ? 'Agregando' : 'Eliminando'} permiso:`, {
            userId,
            modulo,
            permiso,
            action,
            headers: config.headers
        });
        
        let response;
        if (action === 'add') {
            // POST request - body como segundo parámetro
            response = await axios.post(url, data, config);
        } else {
            // DELETE request - data va en la configuración
            response = await axios.delete(url, {
                ...config,
                data: data
            });
        }
        
        console.log('✅ Permiso actualizado exitosamente');
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('❌ Error al actualizar permiso:', error);
        console.error('Response data:', error.response?.data);
        console.error('Response status:', error.response?.status);
        return {
            success: false,
            error: error.response?.data?.message || 'Error al actualizar permiso'
        };
    }
};

/**
 * Procesa los datos de usuarios para la tabla
 * @param {Array} usuarios - Lista de usuarios de la API
 * @param {Array} modulos - Lista de módulos disponibles
 * @param {Array} permisos - Lista de permisos disponibles
 * @returns {Array} - Usuarios procesados para la tabla
 */
export const processUsuariosForTable = (usuarios, modulos, permisos) => {
    if (!usuarios || !modulos || !permisos) {
        console.warn('⚠️ processUsuariosForTable: Datos faltantes', { usuarios: !!usuarios, modulos: !!modulos, permisos: !!permisos });
        return [];
    }

    // console.log('🔄 Procesando usuarios para tabla:', {
    //     usuarios: usuarios.length,
    //     modulos: modulos.length,
    //     permisos: permisos.length
    // });

    return usuarios.map(usuario => {
        // Crear objeto con información básica del usuario
        const processedUser = {
            id: usuario.id,
            nombre: usuario.nombre,
            email: usuario.email,
            activo: usuario.activo,
            usuario: usuario.usuario, // UID de Firebase
            modulos: {}
        };

        // Inicializar todos los módulos sin permisos
        modulos.forEach(modulo => {
            processedUser.modulos[modulo.modulo] = {
                modulo_id: modulo.id,
                descripcion: modulo.descripcion,
                permisos: []
            };
        });

        // Agregar permisos existentes del usuario
        // La API devuelve los permisos en el campo 'permisos', no 'modulos'
        const permisosUsuario = usuario.permisos || usuario.modulos;
        
        if (permisosUsuario && Array.isArray(permisosUsuario) && permisosUsuario.length > 0) {
            // console.log(`📋 Procesando permisos para usuario ${usuario.nombre}:`, permisosUsuario.length);
            
            permisosUsuario.forEach(moduloUsuario => {
                if (processedUser.modulos[moduloUsuario.modulo]) {
                    processedUser.modulos[moduloUsuario.modulo].permisos = moduloUsuario.permisos || [];
                    // console.log(`  ✅ Módulo ${moduloUsuario.modulo}: ${moduloUsuario.permisos?.length || 0} permisos`);
                } else {
                    // console.log(`  ⚠️ Módulo ${moduloUsuario.modulo} no encontrado en lista de módulos disponibles`);
                }
            });
        } else {
            // console.log(`⚠️ Usuario ${usuario.nombre} sin permisos o estructura incorrecta`);
        }

        // console.log('✅ Usuario procesado:', processedUser.nombre, 'con', Object.keys(processedUser.modulos).length, 'módulos');
        return processedUser;
    });
};

/**
 * Verifica si un usuario tiene un permiso específico en un módulo
 * @param {Object} usuario - Usuario procesado
 * @param {string} modulo - Nombre del módulo
 * @param {string} permiso - Nombre del permiso
 * @returns {boolean} - true si tiene el permiso
 */
export const userHasPermission = (usuario, modulo, permiso) => {
    if (!usuario.modulos[modulo]) return false;
    
    const moduloData = usuario.modulos[modulo];
    const hasAdministrar = moduloData.permisos.some(p => p.permiso === 'administrar');
    
    // Si tiene 'administrar', tiene todos los permisos
    if (hasAdministrar) return true;
    
    // Verificar permiso específico
    return moduloData.permisos.some(p => p.permiso === permiso);
};

/**
 * Obtiene todos los permisos efectivos de un usuario en un módulo
 * (incluyendo los implícitos por 'administrar')
 * @param {Object} usuario - Usuario procesado
 * @param {string} modulo - Nombre del módulo
 * @param {Array} todosLosPermisos - Lista completa de permisos disponibles
 * @returns {Array} - Permisos efectivos
 */
export const getEffectivePermissions = (usuario, modulo, todosLosPermisos) => {
    if (!usuario.modulos[modulo]) return [];
    
    const moduloData = usuario.modulos[modulo];
    const hasAdministrar = moduloData.permisos.some(p => p.permiso === 'administrar');
    
    // Si tiene 'administrar', devolver todos los permisos
    if (hasAdministrar) {
        return todosLosPermisos;
    }
    
    // Devolver solo los permisos explícitos
    return moduloData.permisos;
};
